import type { ValueEvent } from '@adornis/base/utilTypes.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import type { DataViewOptions } from '@adornis/data-view/client/data-view-options.js';
import { View } from '@adornis/data-view/db/view.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { DateTimeAspect } from '@adornis/filter/aspect-schemas/DateTimeAspect.js';
import { StringAspect } from '@adornis/filter/aspect-schemas/StringAspect.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { allTinyMCEPlugins } from '@adornis/writer/x-tinymce.js';
import { ChromiumDialog } from '@chancenwerk/client/design/components/cr-dialog.js';
import { ChromiumSnackbar } from '@chancenwerk/client/design/components/cr-snackbar.js';
import { ellipsis } from '@chancenwerk/client/util.js';
import '@chancenwerk/client/components/chancen-component-card-title.js';
import { CrPage } from '@chancenwerk/client/design/components/cr-page.js';
import { projectConfig } from '@chancenwerk/config/config.js';
import { ChancenPermission } from '@chancenwerk/shared/permissions.js';
import { WorkshopAPI } from '@chancenwerk/shared/workshops/api/WorkshopAPI.js';
import { WorkshopAppointment } from '@chancenwerk/shared/workshops/entities/WorkshopAppointment.js';
import { WorkshopAttendanceStatus } from '@chancenwerk/shared/workshops/entities/WorkshopAttendanceStatus.js';
import { WorkshopTrainingAttendance } from '@chancenwerk/shared/workshops/entities/WorkshopTrainingAttendance.js';
import { RaumRoutes } from '@chancenwerk/routes/raumRoutes.js';
import { html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { guard } from 'lit/directives/guard.js';
import { switchMap, tap } from 'rxjs';
import { AkademieDialogWorkshopAppointmentEdit } from '../components/dialogs/akademie-dialog-workshop-appointment-edit.js';

/**
 * @customElement akademie-page-workshop-appointment
 */
@customElement('akademie-page-workshop-appointment')
export class AkademiePageWorkshops extends CrPage {
  @property() appointmentID = new RXController(this, '');

  @state() private readonly _workshopAppointment = new RXController(
    this,
    this.appointmentID.observable.pipe(
      switchMap(id =>
        WorkshopAppointment.subscribeByID<WorkshopAppointment>(id ?? '')({
          ...WorkshopAppointment.allFields,
          attendance: { _id: 1, userID: 1, state: 1 },
          workshop: { title: 1 },
        }),
      ),
      tap(appointment => {
        this.label = `Termin: ` + ellipsis(appointment?.workshop?.title ?? 'Workshop', 60);
        this.description = `Termin am ${appointment?.start?.toFormat('dd.MM.yyyy HH:mm') ?? 'unbekannt'}`;
      }),
    ),
  );
  @state() private readonly _mailMessage = new RXController(this, '');

  constructor() {
    super();

    this.label = 'Workshop Termin';
    this.description = '';
    this.permission = ChancenPermission.akademie.EDIT_WORKSHOP;
    this.backRoute = '/appointments';
  }

  override header() {
    return html`<cr-flex horizontal space="md">
      <cr-button
        @click=${async () => {
          await AkademieDialogWorkshopAppointmentEdit.showPopup({
            props: { workshopAppointment: this._workshopAppointment },
          });
        }}
        >Bearbeiten</cr-button
      >
      <cr-button
        icon="delete"
        tone="error"
        @click=${async () => {
          if (!this._workshopAppointment.value?._id) {
            return;
          }

          if (
            !(await ChromiumDialog.confirm(
              'Möchtest du den Termin wirklich löschen? Alle Anmeldungen werden auch gelöscht!',
            ))
          ) {
            return;
          }

          if (
            (this._workshopAppointment.value.attendance ?? []).length > 0 &&
            (await ChromiumDialog.confirm('Soll eine Benachrichtigung an die Teilnehmer gesendet werden?'))
          ) {
            try {
              await WorkshopAPI.sendParticipationEmail(
                this._workshopAppointment.value._id,
                'Der Termin wurde gelöscht und ist somit storniert!',
                this._workshopAppointment.value.attendance?.map(a => a.userID ?? '') ?? [],
              )();
            } catch (e: any) {
              ChromiumSnackbar.show(e.message);
            }
          }

          for (const a of this._workshopAppointment.value?.attendance ?? []) {
            await a.remove();
          }
          await this._workshopAppointment.value?.remove();

          goTo('/appointments');
        }}
        >Löschen</cr-button
      >
    </cr-flex>`;
  }

  private _table() {
    if (!this._workshopAppointment.value?.start) {
      return html`<cr-card padding="md" space="md">
        <chancen-component-card-title> Anmeldungen </chancen-component-card-title>
        <cr-text bold>Dem Termin fehlt ein Startdatum!</cr-text>
      </cr-card>`;
    }

    return html` <cr-card padding="md" space="md">
      <chancen-component-card-title> Anmeldungen </chancen-component-card-title>
      <cr-data-view
        .options=${{
          class: WorkshopTrainingAttendance,
          view: new View<typeof WorkshopTrainingAttendance>({
            filter: new AdornisFilter({
              aspects: [
                new StringAspect({
                  mode: 'equals',
                  fieldName: 'workshopID',
                  value: this._workshopAppointment.value?.workshopID,
                }),
                new DateTimeAspect({
                  mode: 'range',
                  fieldName: 'date',
                  firstValue: this._workshopAppointment.value?.start?.startOf('day').toJSDate(),
                  secondValue: this._workshopAppointment.value?.start?.endOf('day').toJSDate(),
                }),
              ],
            }),
          }),
          isEntryClickable: false,
          onlyShowLastTranslation: true,
          customColumns: [
            {
              columnName: '',
              customID: 'actions',
              columnWidth: 'max-content',
              selectionSet: { userID: 1 },
              getFieldMapping: w => {
                return html`<cr-button
                  mode="icon"
                  icon="visibility"
                  href=${`${projectConfig.get('RAUM_URL')}${RaumRoutes.editEmployee.substring(1)}/${w.userID}`}
                ></cr-button>`;
              },
            },
            {
              columnName: 'Teilnehmer',
              customID: 'guest',
              selectionSet: { user: { vorname: 1, nachname: 1 } },
              getFieldMapping: w => `${w.user?.vorname ?? ''} ${w.user?.nachname ?? ''}`,
            },
            { columnName: 'Email', fieldName: 'user.email' },
            { columnName: 'Handy', fieldName: 'user.handynummer' },
            { columnName: 'Eingetragen', fieldName: 'createdAt' },
            {
              columnName: 'Status',
              customID: 'state',
              columnWidth: '250px',
              selectionSet: { state: 1 },
              getFieldMapping: w => {
                return html`<cr-dropdown-selection
                  .value=${w.state}
                  .selectables=${Object.values(WorkshopAttendanceStatus)}
                  @value-picked=${async (e: ValueEvent<WorkshopAttendanceStatus>) => {
                    w.state = e.detail.value;
                    await w.save();
                    await ChromiumSnackbar.show('Status gespeichert');
                  }}
                ></cr-dropdown-selection>`;
              },
            },
          ],
        } as DataViewOptions<typeof WorkshopTrainingAttendance>}
      >
      </cr-data-view>
    </cr-card>`;
  }

  private _mail() {
    return html` <cr-card padding="md" space="md">
      <chancen-component-card-title> Benachrichtigung </chancen-component-card-title>
      <cr-text>
        Hier können Sie eine Benachrichtigung für die Teilnehmer des Workshops erstellen. Diese wird per E-Mail
        versendet, an alle Teilnehmer, die nicht storniert haben.
      </cr-text>
      <x-tinymce
        .value=${this._mailMessage.value}
        @value-picked=${async (e: ValueEvent<string>) => {
          this._mailMessage.next(e.detail.value);
        }}
        .editorOptions=${{
          convert_urls: false,
          plugins: allTinyMCEPlugins,
          toolbar:
            'undo redo | bold italic underline strikethrough | fontfamily fontsize blocks | alignleft aligncenter alignright alignjustify | outdent indent lineheight | numlist bullist | forecolor backcolor removeformat | charmap emoticons | link | ltr rtl',
        }}
      ></x-tinymce>
      <cr-button
        ${css({ maxWidth: '300px' })}
        ?disabled=${this._mailMessage.value.length === 0}
        @click=${async () => {
          if (this._mailMessage.value.length === 0) {
            return ChromiumSnackbar.show('Nachricht darf nicht leer sein');
          }

          const attending = (this._workshopAppointment.value?.attendance ?? []).filter(a => {
            return a.state !== WorkshopAttendanceStatus.CANCELLED;
          });
          if (attending.length === 0) {
            return ChromiumSnackbar.show('Keine Teilnehmer um Benachrichtigung zu senden');
          }

          await ChromiumDialog.guard(
            async () => {
              await WorkshopAPI.sendParticipationEmail(
                this.appointmentID.value ?? '',
                this._mailMessage.value,
                attending.map(a => a.userID ?? ''),
              )();

              this._mailMessage.next('');
            },
            {
              hasToConfirm: true,
              confirmText: `Möchtest du die Benachrichtigung wirklich an ${attending.length} Teilnehmer senden?`,
            },
          );
        }}
      >
        Benachrichtigung senden
      </cr-button>
    </cr-card>`;
  }

  override content() {
    return html`<cr-flex padding="sm" space="md">
      ${guard([this._workshopAppointment.value], () => this._table())} ${this._mail()}
    </cr-flex>`;
  }
}
